import { NavigatorContainer } from './navigatorContainer';
import React, { useEffect, useState, JSX } from 'react';
import { getDefaultState } from '../streams/defaultState';
import {
  mapToNavigatorContainerProps,
  subscribeToNavigatorStreamAndRoutines,
  toTreeOptions,
} from '../subscribeToNavigatorStreamAndRoutines';
import {
  EmptyFilterResultProps,
  LoadDataErrorProps,
  LoadDataNotificationProps,
  NavigatorContainerProps,
} from './PropTypes';
import { NavigatorConfiguration } from '../types';
import { dispatchAction } from '@ardoq/rxbeach';
import { streamInitialized } from '../actions/navigatorActions';
import { DEFAULT_ARDOQ_I18NLOCALE, t } from '@ardoq/i18n';
import {
  translateLoadErrorMessage,
  translateLoadingMessage,
} from '../streams/loadStatusKey';

type NavigatorProps = {
  dragElementRoot?: HTMLElement;
  navigatorConfiguration: NavigatorConfiguration;
  LoadDataNotification?: (props: LoadDataNotificationProps) => JSX.Element;
  LoadDataError?: (props: LoadDataErrorProps) => JSX.Element;
  EmptyFilterResult?: (props: EmptyFilterResultProps) => JSX.Element;
};

export const Navigator = React.memo(
  ({
    dragElementRoot,
    navigatorConfiguration,
    LoadDataError,
    LoadDataNotification,
    EmptyFilterResult,
  }: NavigatorProps) => {
    const [state, setState] = useState<NavigatorContainerProps>(() =>
      mapToNavigatorContainerProps({
        navigatorState: getDefaultState(toTreeOptions(navigatorConfiguration)),
        navigatorConfiguration,
      })
    );

    useEffect(() => {
      const unsubscribeStreamAndRoutinesAndCleanUp =
        subscribeToNavigatorStreamAndRoutines({
          setState,
          navigatorConfiguration,
        });
      dispatchAction(
        streamInitialized(),
        navigatorConfiguration.actionNamespace
      );
      return unsubscribeStreamAndRoutinesAndCleanUp;
    }, [setState, navigatorConfiguration]);

    const i18nLocale =
      navigatorConfiguration.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;

    if (state.dataLoadingMessage && LoadDataNotification) {
      return (
        <LoadDataNotification
          loadingScopeDataNotification={translateLoadingMessage(
            i18nLocale,
            state.dataLoadingMessage
          )}
        />
      );
    }

    if (state.dataErrorMessage && LoadDataError) {
      return (
        <LoadDataError
          errorMessage={translateLoadErrorMessage(
            i18nLocale,
            state.dataErrorMessage
          )}
        />
      );
    }

    if (state.isFilterResultEmpty && EmptyFilterResult) {
      return (
        <EmptyFilterResult
          message={t(
            i18nLocale,
            'componentHierarchies:navigator.emptyFilterResult',
            'No components match your search'
          )}
        />
      );
    }

    return (
      <NavigatorContainer
        {...state}
        dragElementRoot={dragElementRoot}
        backgroundColor={navigatorConfiguration.backgroundColor}
      />
    );
  }
);
